/**
 * SDK Configuration Management
 */

import type { ShoppexConfig, ShoppexInitOptions } from '../types';
import { NotInitializedError, ValidationError } from './errors';
import { resetTypedClient } from './typed-client';
import { normalizeRequestedCurrency } from '../utils/requested-currency';

export const DEFAULT_API_BASE_URL = 'https://api.shoppex.io';

let currentConfig: ShoppexConfig | null = null;
let cachedShopId: string | null = null;

export const DEFAULT_CHECKOUT_BASE_URL = 'https://checkout.shoppex.io';

export function initConfig(
  storeSlug: string,
  options?: ShoppexInitOptions
): ShoppexConfig {
  // Validate before touching any module state: a rejected init must leave
  // the previous configuration fully intact, not half of it.
  const currency = normalizeInitCurrency(options?.currency);
  const normalizedShopId = options?.shopId?.trim();

  // Reset cached shop id on every init to avoid cross-store leakage
  // when the SDK is re-initialized with a different slug.
  cachedShopId = normalizedShopId ? normalizedShopId : null;

  currentConfig = {
    storeSlug,
    locale: options?.locale,
    currency,
    apiBaseUrl: options?.apiBaseUrl ?? DEFAULT_API_BASE_URL,
    checkoutBaseUrl: options?.checkoutBaseUrl ?? DEFAULT_CHECKOUT_BASE_URL,
  };
  // Response cache keys carry the locale and buyer currency (client.ts), so a
  // re-init with another audience reads its own entries instead of the
  // previous audience's payloads.
  resetTypedClient();
  return currentConfig;
}

/**
 * The init currency is a contract input (ADR-0066): it is sent on every priced
 * read and refused by the API when the shop does not sell in it. A value that
 * cannot be a currency code is a programming error worth failing at init, not
 * a request the backend should have to reject on every call.
 */
function normalizeInitCurrency(value: string | undefined): string | undefined {
  if (value === undefined || value.trim() === '') {
    return undefined;
  }
  const normalized = normalizeRequestedCurrency(value);
  if (!normalized) {
    throw new ValidationError(
      `Invalid currency "${value}": pass an ISO 4217 code such as "EUR".`,
      ['currency'],
    );
  }
  return normalized;
}

export function getConfig(): ShoppexConfig {
  if (!currentConfig) {
    throw new NotInitializedError();
  }
  return currentConfig;
}

export function isInitialized(): boolean {
  return currentConfig !== null;
}

export function resetConfig(): void {
  currentConfig = null;
  cachedShopId = null;
  resetTypedClient();
}

export function setShopId(shopId: string): void {
  cachedShopId = shopId;
}

export function getShopId(): string | null {
  return cachedShopId;
}
